import React from 'react';
import { Alert, Box, Button, Chip, LinearProgress, Stack, Typography } from '@mui/joy';
import { CheckCircle, Trash, Warning } from '@phosphor-icons/react';
import type { AutoDrawRun } from '../../types';

const STAGE_LABELS: Record<string, string> = {
  '': 'Working…',
  fetching_imagery: 'Fetching aerial imagery…',
  detecting_perimeter: 'Detecting roof outline…',
  classifying_lines: 'Classifying roof lines…',
  deriving_pitch: 'Deriving pitch…',
};

const METHOD_LABELS: Record<string, string> = {
  solar_dsm: 'Google Solar 3D',
  ai_vision: 'AI Vision',
  footprint_only: 'Building footprint',
};

interface AutoDrawReviewBannerProps {
  run: AutoDrawRun;
  onAccept: () => void;
  onReject: () => void;
  onRetry?: () => void;
  accepting?: boolean;
  rejecting?: boolean;
}

/**
 * Progress + review banner for an Auto-draw run. While the run is active it
 * shows the pipeline stage; when detection completes it holds the review
 * gate: detected geometry stays dashed on the map (and blocked from reports)
 * until the user accepts or discards it here.
 */
export const AutoDrawReviewBanner: React.FC<AutoDrawReviewBannerProps> = ({
  run,
  onAccept,
  onReject,
  onRetry,
  accepting = false,
  rejecting = false,
}) => {
  if (run.status === 'queued' || run.status === 'processing') {
    return (
      <Alert color="primary" variant="soft" sx={{ mb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
        <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
          Auto-draw in progress — {STAGE_LABELS[run.progress_stage] || STAGE_LABELS['']}
        </Typography>
        <LinearProgress />
      </Alert>
    );
  }

  if (run.status === 'failed') {
    return (
      <Alert color="danger" variant="soft" sx={{ mb: 2, alignItems: 'flex-start' }} startDecorator={<Warning size={18} />}>
        <Box sx={{ flex: 1 }}>
          <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
            Auto-draw couldn't detect this roof
          </Typography>
          <Typography level="body-xs">{run.error_message}</Typography>
        </Box>
        {onRetry && (
          <Button size="sm" variant="outlined" color="danger" onClick={onRetry}>
            Retry
          </Button>
        )}
      </Alert>
    );
  }

  if (run.status !== 'completed') {
    return null;
  }

  const lineCount = Object.values(run.detected_counts?.features_by_type || {}).reduce(
    (sum, n) => sum + n,
    0
  );

  return (
    <Alert
      color="warning"
      variant="soft"
      sx={{ mb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}>
        <Box>
          <Typography level="body-sm" sx={{ fontWeight: 'bold' }}>
            Review auto-drawn measurements
          </Typography>
          <Typography level="body-xs">
            Detected {run.detected_counts?.sections || 0} roof outline
            {lineCount > 0 ? ` and ${lineCount} lines` : ''} — adjust anything that looks off
            (use Edit mode to drag corners), then accept. Sitting slightly off the roof?
            Hold Shift and tap the arrow keys to slide everything together. Reports stay
            locked until you accept.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
          <Button
            size="sm"
            color="success"
            startDecorator={<CheckCircle size={16} />}
            loading={accepting}
            disabled={rejecting}
            onClick={onAccept}
          >
            Accept
          </Button>
          <Button
            size="sm"
            variant="outlined"
            color="danger"
            startDecorator={<Trash size={16} />}
            loading={rejecting}
            disabled={accepting}
            onClick={onReject}
          >
            Discard
          </Button>
        </Stack>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
        {run.method && (
          <Chip size="sm" variant="outlined" color="neutral">
            Source: {METHOD_LABELS[run.method] || run.method}
          </Chip>
        )}
        {run.imagery_capture_date && (
          <Chip size="sm" variant="outlined" color="neutral">
            Imagery captured {run.imagery_capture_date}
          </Chip>
        )}
        {run.suggested_pitch && (
          <Chip size="sm" variant="outlined" color="primary">
            Suggested pitch: {run.suggested_pitch}
          </Chip>
        )}
        {(run.warnings || []).map((warning, i) => (
          <Chip key={i} size="sm" variant="soft" color="warning" startDecorator={<Warning size={12} />}>
            {warning}
          </Chip>
        ))}
      </Stack>
    </Alert>
  );
};
